
// Deployment trigger: 2026-04-24
import React from 'react';
import { Dices, Zap, Crown } from 'lucide-react';
import { Prize } from '../../types';

interface SlotMachineProps {
  prizes: Prize[];
  slotValues: number[];
  gameState: string;
  onStart: () => void;
  theme?: string;
  isLightMode?: boolean;
}

const SlotMachine: React.FC<SlotMachineProps> = ({ prizes, slotValues, gameState, onStart, theme = 'casino', isLightMode }) => {
  const isCarnival = theme === 'carnival';
  const isNeon = theme === 'neon';
  const isRetro = theme === 'retro';
  const isLight = isLightMode;

  let bgColor = 'bg-[#0b0f1a]';
  let borderColor = 'border-[#1e293b]';
  let iconColor = 'text-indigo-400';
  let slotContainerBg = 'bg-slate-900';
  let slotContainerBorder = 'border-slate-800';
  let slotBg = 'bg-black/40';
  let slotBorder = 'border-white/5';
  let buttonBg = 'bg-indigo-600 hover:bg-indigo-700 text-white';
  let Icon = Dices;

  if (isLight) {
    bgColor = 'bg-white';
    borderColor = 'border-slate-200';
    iconColor = 'text-indigo-600';
    slotContainerBg = 'bg-slate-50';
    slotContainerBorder = 'border-slate-200';
    slotBg = 'bg-white';
    slotBorder = 'border-slate-200';
    buttonBg = 'bg-indigo-600 hover:bg-indigo-700 text-white';
    Icon = Dices;
  } else if (isCarnival) {
    bgColor = 'bg-[#5c1a1b]';
    borderColor = 'border-[#7a2224]';
    iconColor = 'text-yellow-400';
    slotContainerBg = 'bg-[#3d1112]';
    slotContainerBorder = 'border-[#eab308]';
    slotBg = 'bg-white/10';
    slotBorder = 'border-yellow-400/30';
    buttonBg = 'bg-[#eab308] hover:bg-[#ca8a04] text-[#7a2224]';
    Icon = Crown;
  } else if (isNeon) {
    bgColor = 'bg-black';
    borderColor = 'border-fuchsia-600 shadow-[0_0_15px_#c026d3]';
    iconColor = 'text-cyan-400 drop-shadow-[0_0_8px_#22d3ee]';
    slotContainerBg = 'bg-slate-900';
    slotContainerBorder = 'border-cyan-400 shadow-[0_0_15px_#22d3ee]';
    slotBg = 'bg-black/60';
    slotBorder = 'border-fuchsia-500/50';
    buttonBg = 'bg-fuchsia-600 hover:bg-fuchsia-500 text-white shadow-[0_0_15px_#c026d3]';
    Icon = Zap;
  } else if (isRetro) {
    bgColor = 'bg-[#2e1065]';
    borderColor = 'border-[#4c1d95]';
    iconColor = 'text-[#14b8a6]';
    slotContainerBg = 'bg-[#1e1b4b]';
    slotContainerBorder = 'border-[#f97316]';
    slotBg = 'bg-black/50';
    slotBorder = 'border-[#14b8a6]/40';
    buttonBg = 'bg-[#f97316] hover:bg-[#ea580c] text-white border-b-4 border-[#c2410c]';
    Icon = Dices;
  }

  return (
    <div className={`flex flex-col items-center w-full px-6 py-12 ${bgColor} rounded-[48px] shadow-2xl border ${borderColor}`}>
      <Icon className={`w-12 h-12 ${iconColor} mb-8`} />
      <div className={`flex gap-4 p-8 ${slotContainerBg} rounded-[40px] border-4 ${slotContainerBorder} mb-8 w-full max-w-sm shadow-inner relative overflow-hidden`}>
        <div className={`absolute inset-0 bg-gradient-to-b ${isLight ? 'from-slate-200/40 via-transparent to-slate-200/40' : 'from-black/40 via-transparent to-black/40'} pointer-events-none z-10`} />
        {[0, 1, 2].map(i => (
          <div key={i} className={`flex-1 ${slotBg} rounded-2xl h-32 flex items-center justify-center border ${slotBorder} shadow-xl`}>
            <div className={`${isLight ? 'text-slate-800' : 'text-white'} font-black text-[10px] uppercase text-center px-2 ${gameState === 'spinning' ? 'animate-pulse' : ''}`}>
               {prizes[slotValues[i]]?.name}
            </div>
          </div>
        ))}
      </div>
      <button 
        disabled={gameState !== 'idle'} 
        onClick={onStart} 
        className={`${buttonBg} px-14 py-6 rounded-[28px] font-black uppercase tracking-[0.2em] shadow-2xl active:scale-95 transition-all`}
      >
        {gameState === 'idle' ? 'PULL LEVER' : 'SPINNING...'}
      </button>
    </div>
  );
};

export default SlotMachine;
